/**
 * usePostHog — GDPR/Personopplysningsloven-compliant PostHog wrapper.
 *
 * PostHog is ONLY initialised after the user has given explicit consent.
 * Consent is stored in localStorage under the key `analytics_consent`.
 *
 * Norwegian law (Personopplysningsloven implements GDPR) requires:
 *  - A clear legal basis before collecting any usage data. We use consent (Art. 6(1)(a)).
 *  - The ability to withdraw consent at any time.
 *  - Data minimisation — we disable IP capture and use EU data hosting.
 *
 * Configuration choices:
 *  - api_host: eu.i.posthog.com  → data stays in the EU (EEA adequacy)
 *  - person_profiles: 'identified_only'  → no anonymous user profiles
 *  - capture_pageview: false  → we fire manual $pageview events via the router
 *  - persistence: 'localStorage'  → avoids setting any cookies
 *  - ip: false  → do not capture the client IP address
 *  - session_recording: enabled  → records screen interactions for bug diagnosis;
 *    may capture on-screen data including customer names and inventory details.
 *  - capture_exceptions: true  → automatically captures unhandled JS errors and
 *    promise rejections and sends them to PostHog as $exception events.
 */

import posthog from 'posthog-js'
import { computed, readonly, ref } from 'vue'

const CONSENT_KEY = 'analytics_consent' // stored value: 'accepted' | 'declined'

// ── Module-level singletons (shared across all usePostHog() calls) ─────────
const consentStatus = ref(localStorage.getItem(CONSENT_KEY)) // null | 'accepted' | 'declined'
let phInitialized = false

function initPostHog() {
  if (phInitialized) return
  const key = import.meta.env.VITE_POSTHOG_KEY
  if (!key) {
    if (import.meta.env.DEV) console.warn('[PostHog] VITE_POSTHOG_KEY is not set — analytics disabled.')
    return
  }
  posthog.init(key, {
    api_host: import.meta.env.VITE_POSTHOG_HOST || 'https://eu.i.posthog.com',
    person_profiles: 'identified_only',
    capture_pageview: false,
    capture_pageleave: false,
    persistence: 'localStorage',
    ip: false,
    session_recording: {
      // Session replays help diagnose bugs and UX issues.
      // They may capture on-screen content (customer names, item data etc.).
      // Only enabled after explicit user consent.
      recordCrossOriginIframes: false,
    },
    capture_exceptions: true,
  })
  phInitialized = true
}

// Auto-initialise on load if the user already gave consent in a previous session
if (consentStatus.value === 'accepted') {
  initPostHog()
}

/**
 * Manually capture an exception in PostHog.
 * Safe to call from anywhere (main.js, error boundaries, composables).
 * No-op when PostHog is not initialised (no consent / no key).
 * @param {unknown} error
 * @param {{ [key: string]: unknown }} [context]  Extra properties attached to the event.
 */
export function captureException(error, context = {}) {
  if (!phInitialized) return
  posthog.captureException(error, { properties: context })
}

// ─────────────────────────────────────────────────────────────────────────────

export function usePostHog() {
  /** User accepts analytics tracking. Initialises PostHog if not yet done. */
  function giveConsent() {
    localStorage.setItem(CONSENT_KEY, 'accepted')
    consentStatus.value = 'accepted'
    initPostHog()
    if (phInitialized) posthog.opt_in_capturing()
  }

  /** User declines or withdraws consent. Stops any ongoing capturing. */
  function revokeConsent() {
    localStorage.setItem(CONSENT_KEY, 'declined')
    consentStatus.value = 'declined'
    if (phInitialized) posthog.opt_out_capturing()
  }

  /**
   * Identify the authenticated user. Safe to call after every login;
   * no-op when consent has not been given.
   * @param {string|number} userId
   * @param {{ email?: string, name?: string, tenant?: string }} [properties]
   */
  function identify(userId, properties = {}) {
    if (consentStatus.value === 'accepted' && phInitialized) {
      posthog.identify(String(userId), properties)
    }
  }

  /** Reset PostHog identity — call on logout. */
  function reset() {
    if (phInitialized) posthog.reset()
  }

  /**
   * Send a manual $pageview event. Call from the router afterEach hook.
   * @param {string} url
   */
  function capturePageview(url) {
    if (consentStatus.value === 'accepted' && phInitialized) {
      posthog.capture('$pageview', { $current_url: url })
    }
  }

  /**
   * Send a custom event.
   * @param {string} event
   * @param {Record<string, unknown>} [properties]
   */
  function capture(event, properties = {}) {
    if (consentStatus.value === 'accepted' && phInitialized) {
      posthog.capture(event, properties)
    }
  }

  return {
    /** @type {typeof captureException} */
    captureException,
    /** Reactive consent status: null | 'accepted' | 'declined' */
    consentStatus: readonly(consentStatus),
    /**
     * True when the user has not yet made a choice AND PostHog is configured.
     * Setting no VITE_POSTHOG_KEY disables the banner entirely — the easy way to remove it.
     */
    consentPending: computed(() => !!import.meta.env.VITE_POSTHOG_KEY && consentStatus.value === null),
    /** True when the user has accepted */
    consentAccepted: computed(() => consentStatus.value === 'accepted'),
    giveConsent,
    revokeConsent,
    identify,
    reset,
    capturePageview,
    capture,
  }
}
